import { Skeleton } from "@herefor/design-system";
import { PRIVACY_PAGE_MESSAGES as COPY } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import { circleSettingsQuery } from "../../convex/circleSettings";
import { takePortableExportMutation } from "../../convex/portability";
import { useConnectivity } from "../../offline/offline";
import { PrivacySurface, type PrivacyExportState } from "./PrivacySurface";

export interface PrivacyRouteProps {
  readonly circleId: string;
  readonly publicPlanRef: string;
}

/** Server-gated portable export attached to the plain-language promise that names it. */
export function PrivacyRoute({ circleId, publicPlanRef }: PrivacyRouteProps) {
  const settings = useQuery(circleSettingsQuery, { circleId });
  const takeExport = useMutation(takePortableExportMutation);
  const { actionsPaused } = useConnectivity();
  const [exportState, setExportState] = useState<PrivacyExportState>("idle");

  if (settings === undefined) return <Skeleton label={COPY.heading} lines={5} />;
  if (settings === null) {
    return (
      <PrivacySurface
        backHref={`/c/${publicPlanRef}`}
        canExport={false}
        exportState="idle"
        onExport={() => undefined}
      />
    );
  }

  const canExport = settings.canUseCalendarFeed;
  const exportCopy = () => {
    if (!canExport || actionsPaused || exportState === "working") return;
    setExportState("working");
    void takeExport({ circleId, includeContactMethods: false })
      .then((outcome) => {
        if (outcome.status !== "applied") throw new Error("Portable export was replayed.");
        downloadJson(outcome.value.fileNameBase, outcome.value.json);
        setExportState("ready");
      })
      .catch(() => {
        setExportState("failed");
      });
  };

  return (
    <PrivacySurface
      backHref={`/c/${publicPlanRef}`}
      canExport={canExport}
      exportState={exportState}
      onExport={exportCopy}
    />
  );
}

export function downloadJson(fileNameBase: string, json: string): void {
  const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${fileNameBase}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
